import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import { cautionService } from '../../services/api';
import { Caution } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, EditIcon, TrashIcon } from '../common/Icons';
import CautionForm from './CautionForm';

const CautionList: React.FC = () => {
  const [cautions, setCautions] = useState<Caution[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCaution, setEditingCaution] = useState<Caution | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });

  useEffect(() => {
    fetchCautions();
  }, [filters]);

  const fetchCautions = async () => {
    try {
      setLoading(true);
      const response = await cautionService.getAll(filters);
      setCautions(response.data.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des cautions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette caution ?')) {
      try {
        await cautionService.delete(id);
        toast.success('Caution supprimée avec succès');
        fetchCautions();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await cautionService.updateStatus(id, status);
      toast.success('Statut mis à jour avec succès');
      fetchCautions();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      active: 'primary',
      returned: 'success',
      expired: 'warning',
      lost: 'danger',
    };
    
    const labels: { [key: string]: string } = {
      active: 'Active',
      returned: 'Retournée',
      expired: 'Expirée',
      lost: 'Perdue',
    };

    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Cautions de Marchés</h2>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          <PlusIcon className="me-2" />
          Nouvelle caution
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                <option value="active">Active</option>
                <option value="returned">Retournée</option>
                <option value="expired">Expirée</option>
                <option value="lost">Perdue</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Marché</th>
                  <th>Montant</th>
                  <th>Méthode</th>
                  <th>N° Chèque</th>
                  <th>Date soumission</th>
                  <th>Date expiration</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cautions.map((caution) => (
                  <tr key={caution.id}>
                    <td>{caution.reference}</td>
                    <td>{caution.market_name}</td>
                    <td>{parseFloat(caution.amount.toString()).toFixed(2)} MAD</td>
                    <td>{caution.method === 'cash' ? 'Espèces' : 'Chèque'}</td>
                    <td>
                      {caution.method === 'check' ? (
                        caution.check?.check_number || caution.check?.number || 'N/A'
                      ) : '-'}
                    </td>
                    <td>{new Date(caution.submission_date).toLocaleDateString()}</td>
                    <td>{new Date(caution.expiry_date).toLocaleDateString()}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={caution.status}
                        onChange={(e) => handleStatusChange(caution.id, e.target.value)}
                        style={{ width: 'auto' }}
                      >
                        <option value="active">Active</option>
                        <option value="returned">Retournée</option>
                        <option value="expired">Expirée</option>
                        <option value="lost">Perdue</option>
                      </Form.Select>
                    </td>
                    <td>
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setEditingCaution(caution);
                          setShowForm(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(caution.id)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <CautionForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingCaution(null);
        }}
        onSuccess={() => {
          fetchCautions();
          setShowForm(false);
          setEditingCaution(null);
        }}
        caution={editingCaution}
      />
    </div>
  );
};

export default CautionList;